import { forwardRef, type ButtonHTMLAttributes } from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cn } from '@/utils/cn'
import { Spinner } from './Spinner'

type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost' | 'outline' | 'subtle' | 'outline-primary'
type ButtonSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl'

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  loading?: boolean
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
  asChild?: boolean
  iconOnly?: boolean
}

const variantStyles: Record<ButtonVariant, string> = {
  primary:
    'bg-gradient-to-br from-primary to-primary-dark text-white hover:from-primary-dark hover:to-primary-dark/90 active:from-primary-dark active:to-primary-dark/80 shadow-cta hover:shadow-cta-hover transition-shadow',
  secondary:
    'bg-bg-elevated text-text-primary hover:bg-bg-border border border-bg-border',
  danger:
    'bg-error/10 text-error hover:bg-error/20 border border-error/30',
  ghost:
    'text-text-secondary hover:text-text-primary hover:bg-bg-elevated',
  outline:
    'border border-bg-border text-text-primary hover:bg-bg-elevated',
  subtle:
    'bg-primary/10 text-primary hover:bg-primary/20',
  'outline-primary':
    'border border-primary text-primary hover:bg-primary/10',
}

const sizeStyles: Record<ButtonSize, string> = {
  xs: 'h-8 px-2.5 text-xs rounded gap-1',
  sm: 'h-9 px-3 text-sm rounded gap-1.5',
  md: 'h-10 px-4 text-sm rounded-md gap-2',
  lg: 'h-11 px-5 text-base rounded-md gap-2',
  xl: 'h-12 px-6 text-base rounded-md gap-2',
}

const iconSizeStyles: Record<ButtonSize, string> = {
  xs: 'w-8 h-8 px-0 rounded',
  sm: 'w-9 h-9 px-0 rounded',
  md: 'w-10 h-10 px-0 rounded-md',
  lg: 'w-11 h-11 px-0 rounded-md',
  xl: 'w-12 h-12 px-0 rounded-md',
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'secondary',
      size = 'md',
      loading,
      leftIcon,
      rightIcon,
      disabled,
      children,
      className,
      asChild,
      iconOnly,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : 'button'

    return (
      <Comp
        ref={ref}
        disabled={disabled || loading}
        className={cn(
          'inline-flex items-center justify-center font-medium transition-colors cursor-pointer select-none',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          variantStyles[variant],
          iconOnly ? iconSizeStyles[size] : sizeStyles[size],
          className
        )}
        {...props}
      >
        {loading ? <Spinner size="sm" /> : leftIcon}
        {children}
        {!loading && rightIcon}
      </Comp>
    )
  }
)
Button.displayName = 'Button'
